import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { FileText, FlaskConical, Loader2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { DataBenchJobDescriptionCanvas } from '@/components/platform/data-bench-job-description-canvas'
import { DataBenchJobPanel } from '@/components/platform/data-bench-job-panel'
import { DataBenchJobSkillsCanvas } from '@/components/platform/data-bench-job-skills-canvas'
import { DataBenchPeopleList } from '@/components/platform/data-bench-people-list'
import { DataBenchRemoval } from '@/components/platform/platform-data-bench-removal'
import { DataBenchResults } from '@/components/platform/platform-data-bench-results'
import {
  EmploymentReportCanvas,
  dataBenchDisplayName,
} from '@/components/platform/platform-data-bench-person-canvas'
import { useArtifactCanvas } from '@/components/artifact/artifact-canvas-context'
import { JobOpeningSkillsReadSection } from '@/components/job-opening-skills-read-section'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import {
  PageHeaderActionSecondary,
  PageHeaderActions,
} from '@/components/navigation/page-header-actions'
import { SampleJobArchiveDialog } from '@/components/platform/sample-job-archive-dialog'
import { PaneListSkeleton } from '@/components/layout/pane-list-skeleton'
import { Button } from '@/components/ui/button'
import { EntityAboutSection } from '@/components/ui/entity-about-section'
import { FileUpload } from '@/components/ui/file-upload'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { EntityManageSection } from '@/components/ui/entity-manage-section'
import {
  isSimulationJobActive,
  useArchiveSimulationScenario,
  useGenerateSimulationProfiles,
  useMarkSimulationRubricPreview,
  useRunSimulationPass1,
  useSetSimulationNeedles,
  useSimulationCapabilities,
  useSimulationJob,
  useSimulationJobStream,
  useSimulationScenario,
  useUpdateSimulationJob,
  useUploadSimulationCvs,
  useUploadSimulationJobFile,
  type SimulationProfile,
  type SimulationScenario,
} from '@/hooks/use-platform-simulation'
import { dataBenchCopy, opportunityRequiredSkillsMiss } from '@/lib/platform-data-bench-copy'
import { useDeclareUiSurface } from '@/lib/ui-surface'
import { getPluginSlots } from '@/plugin/slots'

export function DataBenchRunPage({ scenarioId }: { scenarioId: string }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const descriptionOpenKeyRef = useRef(0)
  const skillsOpenKeyRef = useRef(0)
  useDeclareUiSurface({
    intent: 'edit',
    entity: 'data_bench_run',
    id: scenarioId,
    mode: 'page',
    fields: ['title', 'description', 'skills'],
  })
  const { scoreActions } = getPluginSlots().dataBench
  const { data: capabilities } = useSimulationCapabilities()
  const { data: scenario, isLoading, error } = useSimulationScenario(scenarioId)
  const archiveScenario = useArchiveSimulationScenario()
  const updateJob = useUpdateSimulationJob()
  const generateProfiles = useGenerateSimulationProfiles()
  const uploadCvs = useUploadSimulationCvs()
  const uploadJobFile = useUploadSimulationJobFile()
  const setNeedles = useSetSimulationNeedles()
  const markRubric = useMarkSimulationRubricPreview()
  const runPass1 = useRunSimulationPass1()

  const [title, setTitle] = useState('')
  const [genCount, setGenCount] = useState(5)
  const [jobId, setJobId] = useState<string | undefined>()
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>([])

  const hydratedJobId = jobId || scenario?.active_job?.id || undefined
  const jobQuery = useSimulationJob(hydratedJobId, Boolean(hydratedJobId))
  useSimulationJobStream(hydratedJobId, scenarioId)

  const pack = capabilities?.packs?.[0]
  const copy = dataBenchCopy(pack)
  const craftEnabled = capabilities?.craft_enabled !== false
  const isEmployment = scenario
    ? scenario.methodology_id === 'employment_match'
    : copy.isEmployment

  const job = jobQuery.data
  const jobActive = isSimulationJobActive(job?.status) || isSimulationJobActive(scenario?.active_job?.status)
  const jobLost = Boolean(hydratedJobId && jobQuery.isError)

  useEffect(() => {
    if (!scenario) return
    setTitle(scenario.opportunity_title || '')
  }, [scenario?.id, scenario?.opportunity_title])

  useEffect(() => {
    if (scenario?.active_job?.id) setJobId(scenario.active_job.id)
  }, [scenario?.active_job?.id])

  useEffect(() => {
    if (!job) return
    if (job.status === 'completed') {
      if (job.kind === 'pass1') toast.success('Scoring finished — results are below')
      if (job.kind === 'generate') toast.success('People added')
      if (job.kind === 'enrich') toast.success('CV fill finished')
      if (job.kind === 'extract_job_spec') {
        const required = Array.isArray(job.result?.required_skills)
          ? job.result.required_skills.length
          : 0
        if (required > 0) toast.success('Required and preferred skills ready')
        else toast.error('Could not read required skills from this job text')
      }
      void qc.invalidateQueries({ queryKey: ['platform-sim-scenario', scenarioId] })
      void qc.invalidateQueries({ queryKey: ['platform-sim-scenarios'] })
      setJobId(undefined)
    } else if (job.status === 'failed') {
      toast.error(job.error || 'Job failed')
      setJobId(undefined)
    }
  }, [job, qc, scenarioId])

  useEffect(() => {
    if (!jobLost) return
    toast.error('Job lost after restart — retry')
    setJobId(undefined)
  }, [jobLost])

  const peopleCount = scenario?.profiles?.length ?? 0
  const busy =
    jobActive ||
    generateProfiles.isPending ||
    runPass1.isPending ||
    uploadCvs.isPending ||
    uploadJobFile.isPending

  const documentAccept: Record<string, string[]> = {
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'text/plain': ['.txt'],
    'text/markdown': ['.md'],
  }
  const canScore =
    craftEnabled &&
    !busy &&
    (isEmployment ? peopleCount > 0 : (scenario?.conditions?.length ?? 0) > 0 || peopleCount > 0)

  const generatingPlaceholders = useMemo(() => {
    if (job?.kind !== 'generate' || !jobActive) return 0
    const total = job.progress?.total ?? 0
    return Math.max(0, total - peopleCount)
  }, [job?.kind, job?.progress?.total, jobActive, peopleCount])

  const scoringIds = job?.progress?.current_ids ?? []

  const readSkills = useMemo(
    () =>
      (scenario?.opportunity_skills ?? []).map((row) => ({
        id: row.skill_id,
        skill_name: row.skill_name,
        requirement_type: row.requirement_type,
      })),
    [scenario?.opportunity_skills],
  )

  const handleJobSaved = useCallback((updated: SimulationScenario) => {
    const extractId = updated.active_job?.id
    if (extractId && updated.active_job?.kind === 'extract_job_spec') {
      setJobId(extractId)
      toast.message('Reading required and preferred skills…')
    } else {
      toast.success('Saved')
    }
  }, [])

  const saveTitleIfChanged = useCallback(async () => {
    if (!craftEnabled || !scenario) return
    const trimmed = title.trim()
    if (trimmed === (scenario.opportunity_title || '').trim()) return
    if (!trimmed) {
      toast.error('Title is required')
      setTitle(scenario.opportunity_title || '')
      return
    }
    try {
      await updateJob.mutateAsync({ scenarioId, title: trimmed })
      toast.success('Title saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save title')
    }
  }, [craftEnabled, scenario, title, scenarioId, updateJob])

  const openJobDescriptionCanvas = useCallback(() => {
    if (!scenario) return
    descriptionOpenKeyRef.current += 1
    openCanvas({
      title: 'Job description',
      preventAutoFocus: true,
      surface: {
        intent: 'edit',
        entity: 'data_bench_run',
        id: scenarioId,
        mode: 'canvas',
        fields: ['description'],
      },
      content: (
        <DataBenchJobDescriptionCanvas
          key={`sim-job-desc-${descriptionOpenKeyRef.current}`}
          scenarioId={scenarioId}
          title={title}
          description={scenario.job_description || ''}
          onClose={closeCanvas}
          onSaved={handleJobSaved}
        />
      ),
    })
  }, [scenario, openCanvas, scenarioId, title, closeCanvas, handleJobSaved])

  const openJobSkillsCanvas = useCallback(() => {
    if (!scenario) return
    skillsOpenKeyRef.current += 1
    openCanvas({
      title: 'Skills',
      preventAutoFocus: true,
      surface: {
        intent: 'edit',
        entity: 'data_bench_run',
        id: scenarioId,
        mode: 'canvas',
        fields: ['skills'],
      },
      content: (
        <DataBenchJobSkillsCanvas
          key={`sim-job-skills-${skillsOpenKeyRef.current}`}
          scenarioId={scenarioId}
          organisationSlug={scenario.organisation_slug || ''}
          skills={scenario.opportunity_skills ?? []}
          onClose={closeCanvas}
          onSaved={handleJobSaved}
        />
      ),
    })
  }, [scenario, openCanvas, scenarioId, closeCanvas, handleJobSaved])

  async function toggleKnownGood(profileId: string) {
    if (!scenario) return
    const next = new Set(scenario.needle_profile_ids ?? [])
    if (next.has(profileId)) next.delete(profileId)
    else next.add(profileId)
    try {
      await setNeedles.mutateAsync({
        scenarioId,
        needle_profile_ids: [...next],
      })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update known-good mark')
    }
  }

  async function handleArchive() {
    try {
      await archiveScenario.mutateAsync(scenarioId)
      toast.success('Sample job removed')
      setArchiveOpen(false)
      navigate('/platform/simulation')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete sample job')
    }
  }

  function openPerson(profile: SimulationProfile) {
    openCanvas({
      title: dataBenchDisplayName(profile),
      preventAutoFocus: true,
      content: (
        <EmploymentReportCanvas
          profile={profile}
          opportunityTitle={scenario?.opportunity_title}
          initialTab="profile"
        />
      ),
    })
  }

  if (isLoading) {
    return (
      <PageContainer>
        <PaneListSkeleton ariaLabel="Loading run" variant="row" />
      </PageContainer>
    )
  }
  if (error || !scenario) {
    return (
      <PageContainer>
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Run not found'}
        </p>
        <div className="mt-3">
          <Button type="button" variant="outline" onClick={() => navigate('/platform/simulation')}>
            Back
          </Button>
        </div>
      </PageContainer>
    )
  }

  const skillsMiss = opportunityRequiredSkillsMiss(scenario)

  return (
    <PageContainer>
      <PageHeader
        leading={
          <PageHeaderLeading back={{ label: 'Back to Simulation', to: '/platform/simulation' }} />
        }
        title={scenario.name}
        description={
          isEmployment
            ? 'Score these CVs and see who ranks where. You are checking the judgment — take what you learn to Rubrics.'
            : 'Score this sample and see who ranks where. You are checking the judgment — take what you learn to Rubrics.'
        }
        icon={<FlaskConical className="h-5 w-5" />}
        actions={
          <PageHeaderActions
            secondary={
              <PageHeaderActionSecondary
                type="button"
                variant="destructive"
                onClick={() => setArchiveOpen(true)}
                disabled={archiveScenario.isPending}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete
              </PageHeaderActionSecondary>
            }
          />
        }
      />

      <div className="space-y-6">
        {isEmployment ? (
          <EntityManageSection
            title="1. Job"
            subtitle="The job is the test case, not something you are publishing."
          >
            <div className="space-y-6">
              <FormField label="Title" htmlFor="sim-job-title">
                <Input
                  id="sim-job-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={() => void saveTitleIfChanged()}
                  disabled={!craftEnabled || updateJob.isPending}
                />
              </FormField>

              <EntityAboutSection
                title="Job description"
                leadingIcon={FileText}
                subtitle="Full text for this test case"
                body={scenario.job_description}
                stripLeadingTitles={[scenario.opportunity_title || '', title]}
                emptyVariant="cta"
                emptyCtaLabel="Write description"
                onEdit={craftEnabled ? openJobDescriptionCanvas : undefined}
                editAriaLabel="Edit job description"
                editTitle="Edit job description"
              />

              <FileUpload
                multiple={false}
                showSelectedList={false}
                disabled={!craftEnabled || busy}
                accept={documentAccept}
                title={
                  scenario.job_description?.trim()
                    ? 'Drop a file to replace this description'
                    : 'Drop a job description'
                }
                activeTitle="Drop to fill the job"
                description="PDF, Word, or text. We read the file and extract required skills."
                onFilesSelected={(files) => {
                  const file = files[0]
                  if (!file) return
                  void uploadJobFile
                    .mutateAsync({ scenarioId, file })
                    .then((updated) => {
                      toast.success('Job description uploaded')
                      handleJobSaved(updated)
                    })
                    .catch((err: unknown) =>
                      toast.error(err instanceof Error ? err.message : 'Upload failed'),
                    )
                }}
              />

              {skillsMiss ? (
                <p role="status" className="text-sm text-destructive">
                  {skillsMiss}
                </p>
              ) : null}
              <JobOpeningSkillsReadSection
                skills={readSkills}
                onEdit={craftEnabled ? openJobSkillsCanvas : undefined}
                emptyMessage={skillsMiss ? 'Add required skills' : 'Add skills'}
                emptyVariant="cta"
              />

              {job?.kind === 'extract_job_spec' && jobActive ? (
                <DataBenchJobPanel
                  kind="extract_job_spec"
                  status={job.status}
                  progress={job.progress}
                  error={job.error}
                />
              ) : null}
            </div>
          </EntityManageSection>
        ) : (
          <EntityManageSection
            title="1. Sample"
            subtitle="Use a scoring sample pack for patients and conditions. This run does not accept CV or job-description uploads."
          >
            <p className="text-sm text-muted-foreground">
              {(scenario.conditions?.length ?? 0) > 0
                ? `${scenario.conditions?.length} conditions on ${peopleCount} ${copy.poolNoun}.`
                : `No conditions attached. Create a scoring sample from the Simulation hub.`}
            </p>
          </EntityManageSection>
        )}

        {isEmployment ? (
          <EntityManageSection
            title="2. People"
            subtitle="Upload CVs to demonstrate profile fill, or add generated people."
          >
            <div className="space-y-4">
              <FileUpload
                multiple
                disabled={!craftEnabled || !copy.supportsCv || busy}
                accept={documentAccept}
                title="Drop CVs here"
                activeTitle="Drop to fill profiles"
                description="Each file becomes a person. Watch the row fill from the CV, then open them before you score."
                onFilesSelected={(files) => {
                  if (files.length === 0) return
                  void uploadCvs
                    .mutateAsync({ scenarioId, files })
                    .then((updated) => {
                      toast.success(`Uploaded ${files.length} CV${files.length === 1 ? '' : 's'}`)
                      const enrichId = updated.active_job?.id
                      if (enrichId) setJobId(enrichId)
                    })
                    .catch((err: unknown) =>
                      toast.error(err instanceof Error ? err.message : 'Upload failed'),
                    )
                }}
              />
              <div className="flex flex-wrap items-end gap-2">
                <FormField label="Generated people" htmlFor="gen-n">
                  <Input
                    id="gen-n"
                    type="number"
                    className="w-24"
                    min={1}
                    max={50}
                    value={genCount}
                    onChange={(e) => setGenCount(Number(e.target.value) || 1)}
                    disabled={busy}
                  />
                </FormField>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!craftEnabled || busy}
                  onClick={() => {
                    void generateProfiles
                      .mutateAsync({ scenarioId, count: genCount, async_job: true })
                      .then((result) => {
                        if ('kind' in result && result.id && result.id !== 'sync') {
                          setJobId(result.id)
                        } else {
                          toast.success('People added')
                        }
                      })
                      .catch((err: unknown) =>
                        toast.error(err instanceof Error ? err.message : 'Could not add people'),
                      )
                  }}
                >
                  {generateProfiles.isPending || (job?.kind === 'generate' && jobActive) ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  {generateProfiles.isPending || (job?.kind === 'generate' && jobActive)
                    ? 'Generating people…'
                    : 'Add generated people'}
                </Button>
              </div>
              {job && (job.kind === 'generate' || job.kind === 'enrich') && jobActive ? (
                <DataBenchJobPanel
                  kind={job.kind}
                  status={job.status}
                  progress={job.progress}
                  error={job.error}
                />
              ) : null}
              <p className="text-sm text-muted-foreground">
                {peopleCount} {copy.poolNoun} in this pool.
              </p>
              {peopleCount > 0 || generatingPlaceholders > 0 ? (
                <DataBenchPeopleList
                  profiles={scenario.profiles ?? []}
                  job={job}
                  generatingPlaceholders={generatingPlaceholders}
                  scoringIds={scoringIds}
                  onOpen={openPerson}
                  selectedIds={selectedProfileIds}
                  onToggleSelected={(profileId) =>
                    setSelectedProfileIds((current) =>
                      current.includes(profileId)
                        ? current.filter((id) => id !== profileId)
                        : [...current, profileId],
                    )
                  }
                />
              ) : null}
            </div>
          </EntityManageSection>
        ) : null}

        <DataBenchRemoval
          scenarioId={scenarioId}
          selectedProfileIds={selectedProfileIds}
          onSelectedProfileIdsChange={setSelectedProfileIds}
        />

        <EntityManageSection
          title={isEmployment ? '3. Score' : '2. Score'}
          subtitle="See who comes out on top. You are checking the judgment, not hiring anyone."
        >
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              disabled={!canScore}
              onClick={() => {
                void runPass1
                  .mutateAsync({ scenarioId, async_job: true })
                  .then((result) => {
                    if (result.id && result.id !== 'sync') setJobId(result.id)
                    else toast.success('Scoring finished — results are below')
                  })
                  .catch((err: unknown) =>
                    toast.error(err instanceof Error ? err.message : 'Scoring failed'),
                  )
              }}
            >
              {runPass1.isPending || (job?.kind === 'pass1' && jobActive) ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {runPass1.isPending || (job?.kind === 'pass1' && jobActive) ? 'Scoring…' : 'Score'}
            </Button>
            {scenario.status === 'scored' ? (
              <Button
                type="button"
                variant="outline"
                disabled={!craftEnabled || markRubric.isPending}
                onClick={() => {
                  void markRubric
                    .mutateAsync(scenarioId)
                    .then(() => {
                      toast.success('Linked for Rubrics sample analysis')
                      navigate('/platform/evaluation-rubrics')
                    })
                    .catch((err: unknown) =>
                      toast.error(err instanceof Error ? err.message : 'Could not link'),
                    )
                }}
              >
                Use in Rubrics
              </Button>
            ) : null}
            {scoreActions.map((ScoreAction, index) => (
              <ScoreAction
                key={index}
                scenarioId={scenarioId}
                scenario={scenario}
                canScore={canScore}
                jobActive={jobActive}
              />
            ))}
          </div>
          {job?.kind === 'pass1' && jobActive ? (
            <div className="pt-3">
              <DataBenchJobPanel
                kind="pass1"
                status={job.status}
                progress={job.progress}
                error={job.error}
              />
            </div>
          ) : null}
          {!canScore && !jobActive ? (
            <p className="pt-2 text-sm text-muted-foreground">
              {isEmployment
                ? 'Add at least one person before scoring.'
                : 'Create a scoring sample pack with patients and conditions first.'}
            </p>
          ) : null}
        </EntityManageSection>

        <DataBenchResults
          scenario={scenario}
          copy={copy}
          craftEnabled={craftEnabled}
          onToggleKnownGood={(id) => void toggleKnownGood(id)}
        />
      </div>

      <SampleJobArchiveDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        sampleName={scenario.name}
        isPending={archiveScenario.isPending}
        onConfirm={() => void handleArchive()}
      />
    </PageContainer>
  )
}
